// Google branch — OAuth connect (overhaul §6.2, panel 02·Google). Reuses useOAuthPopup +
// google-connect-url exactly like the Sources page. The reliable completion signal is the
// readiness poll (the popup's postMessage is best-effort cross-origin), so we advance once
// the Google integration shows up in readiness. No extension.

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { useOAuthPopup } from '../../hooks/useOAuthPopup';
import { useOnboardingReadiness } from '../../hooks/useOnboardingReadiness';
import { GoogleLogo } from '../shared/Icons';

export function GoogleConnectStep({ onConnected, onBack }: { onConnected: () => void; onBack: () => void }) {
  const { open } = useOAuthPopup();
  const { data, refetch } = useOnboardingReadiness({ pollMs: 3000 });
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (data?.connectedProviders.includes('google') && !fired.current) {
      fired.current = true;
      onConnected();
    }
  }, [data?.connectedProviders, onConnected]);

  const connect = () => {
    setError(null);
    setStarted(true);
    open(
      async () => {
        const { url } = await apiFetch<{ url: string }>(
          '/api/surfaces/sources/google-connect-url',
          { credentials: 'include' }
        );
        return url;
      },
      (payload) => {
        if (!payload.ok) {
          setError(payload.error || 'Failed to connect Google');
          setStarted(false);
        } else {
          void refetch(); // immediate check when the success message does cross origins
        }
      }
    );
  };

  return (
    <>
      <div className="crumb">STEP 02 · GOOGLE</div>
      <div className="wh">Connect <span className="em">Google</span>.</div>
      <p className="wp">
        Read-only Gmail &amp; Docs from the last 90 days. Terum never sends, edits, or deletes
        anything.
      </p>
      <div style={{ marginTop: 22, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={connect}>
          <GoogleLogo size={18} />Authorize with Google
        </button>
        {error ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--rose)' }}>⚠ {error}</span>
        ) : started ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--inkS)' }}>
            <span className="dot acc pulse" /> Authorize in the popup…
          </span>
        ) : null}
      </div>
      <div className="wiz-foot">
        <button className="btn btn-secondary" onClick={onBack}>← Back to sources</button>
        <span className="prog">Step 2 · Google</span>
      </div>
    </>
  );
}
